import { useCallback, useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import '../styles/tour.css';
import { useAuth } from '../contexts/AuthContext';
import { TOUR_STEPS } from '../config/tourSteps';

const STORAGE_KEY_PREFIX = 'tour_seen_';

function createDriverConfig(steps, markTourSeen) {
  return {
    showProgress: true,
    allowClose: true,
    overlayColor: 'rgba(0,0,0,0.5)',
    popoverClass: 'picl-tour-popover',
    nextBtnText: 'Next →',
    doneBtnText: 'Done',
    steps,
    onDestroyStarted: () => {
      markTourSeen();
    },
    onNextClick: (element, step, { state, driver: driverObj }) => {
      const isLastStep = state.activeIndex === steps.length - 1;
      if (isLastStep) {
        markTourSeen();
        driverObj.destroy();
      } else {
        driverObj.moveNext();
      }
    },
    onCloseClick: (element, step, { driver: driverObj }) => {
      markTourSeen();
      driverObj.destroy();
    },
    onPopoverRender: (popover, { driver: driverObj }) => {
      if (popover?.closeButton) {
        popover.closeButton.addEventListener('click', () => {
          markTourSeen();
          driverObj.destroy();
        });
      }
    },
  };
}

export function useTour() {
  const { user } = useAuth();
  const driverRef = useRef(null);
  const hasAutoStarted = useRef(false);
  const lastUserKeyRef = useRef(null);

  const getStepsForRole = useCallback(() => {
    const role = user?.role?.toUpperCase();
    if (role === 'ADMIN') return TOUR_STEPS.ADMIN;
    if (role === 'COORDINATOR') return TOUR_STEPS.COORDINATOR;
    if (role === 'USER') return TOUR_STEPS.USER;
    return [];
  }, [user?.role]);

  const getTourKey = useCallback(() => {
    return user?.id ?? user?.email ?? 'anonymous';
  }, [user?.id, user?.email]);

  const hasSeenTour = useCallback(() => {
    const key = getTourKey();
    if (!key || key === 'anonymous') return true;
    try {
      return localStorage.getItem(`${STORAGE_KEY_PREFIX}${key}`) === '1';
    } catch {
      return false;
    }
  }, [getTourKey]);

  const markTourSeen = useCallback(() => {
    const key = getTourKey();
    if (!key || key === 'anonymous') return;
    try {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${key}`, '1');
    } catch {
      // ignore
    }
  }, [getTourKey]);

  const startTour = useCallback(() => {
    const steps = getStepsForRole();
    if (steps.length === 0) return;

    if (driverRef.current) {
      driverRef.current.destroy();
    }

    driverRef.current = driver(createDriverConfig(steps, markTourSeen));
    driverRef.current.drive();
  }, [getStepsForRole, markTourSeen]);

  const autoStartIfFirstTime = useCallback(() => {
    const key = getTourKey();
    if (!key || key === 'anonymous') return;
    if (hasSeenTour()) return;
    if (hasAutoStarted.current) return;

    const steps = getStepsForRole();
    if (steps.length === 0) return;

    hasAutoStarted.current = true;

    const runTour = () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
      driverRef.current = driver(createDriverConfig(steps, markTourSeen));
      driverRef.current.drive();
    };

    setTimeout(runTour, 600);
  }, [getTourKey, getStepsForRole, hasSeenTour, markTourSeen]);

  useEffect(() => {
    const key = getTourKey();
    if (!key || key === 'anonymous') return;
    if (lastUserKeyRef.current === key) return;
    lastUserKeyRef.current = key;
    hasAutoStarted.current = false;
    autoStartIfFirstTime();
  }, [user, getTourKey, autoStartIfFirstTime]);

  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, []);

  return { startTour, autoStartIfFirstTime, hasSeenTour };
}
